import {
  Grid,
  Card,
  CardHeader,
  CardContent,
  TextField,
  Typography,
  FormControl,
  Tooltip,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { useSelector } from "react-redux";
import { LoadingButton } from "@mui/lab";
import { DataGrid } from "@mui/x-data-grid";
import { useState, useEffect } from "react";
import ClearIcon from "@mui/icons-material/Clear"; // Import the ClearIcon
import axios from "axios";

export const Registration = ({
  title,
  registrationRows,
  setManagementData,
  isSm,
}) => {
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const maxWidth = "90%";
  const [selectedCell, setSelectedCell] = useState(null);
  const [textFieldValue, setTextFieldValue] = useState("");
  const [newCellText, setNewCellText] = useState("");
  const [selectValue, setSelectValue] = useState("");
  const [removeLoading, setRemoveLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [errorText, setErrorText] = useState("");

  useEffect(() => {
    const clearTextInterval = setInterval(() => {
      if (errorText !== "") {
        setErrorText("");
      }
    }, 3000);

    return () => {
      clearInterval(clearTextInterval); // Clear the interval when the component unmounts
    };
  }, [errorText]);

  const handleEdit = async (type) => {
    const dataObj = {
      submitType: type,
      column: selectedCell?.field,
      oldValue: selectedCell?.formattedValue,
      changeValue: textFieldValue,
      addCategory: selectValue,
      addValue: newCellText,
    };

    try {
      if (type === "remove") {
        setRemoveLoading(true);
      } else if (type === "edit") {
        setEditLoading(true);
      } else if (type === "add") {
        setAddLoading(true);
      }

      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/admin/reg-management`,
        {
          dataObj,
          currentSession: currentSession,
        },
        { withCredentials: true }
      );

      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/data/managementData`,
        { currentSession: currentSession },
        { withCredentials: true }
      );

      setManagementData(res.data.data);

      setErrorText(resp.data.message);

      if (type === "remove") {
        setRemoveLoading(false);
      } else if (type === "edit") {
        setEditLoading(false);
      } else if (type === "add") {
        setAddLoading(false);
      }

      setSelectedCell(null);
      setTextFieldValue("");
      setNewCellText("");
      setSelectValue("");
    } catch (e) {}
  };

  const handleCellClick = (params) => {
    setSelectedCell(params);
    setTextFieldValue(params?.formattedValue);
  };

  const renderSelectedValues = (selected) => {
    if (!Array.isArray(selected)) {
      selected = [selected]; // Convert to an array if not already
    }

    if (selected.length === 0) {
      return <em>Select an option</em>;
    }

    // Join the selected items into a string
    const selectedText = selected.join(", ");

    // Define the maximum length you want to display
    const maxLength = 30; // For example, limit to 20 characters

    // Truncate the text if it exceeds the maximum length
    const truncatedText =
      selectedText.length > maxLength
        ? selectedText.substring(0, maxLength) + "..."
        : selectedText;

    return truncatedText;
  };

  const truncateText = (text, maxLength) => {
    if (text.length > maxLength) {
      return text.substring(0, maxLength) + "...";
    }
    return text;
  };

  const registrationColumns = [
    {
      field: "referral_source",
      headerName: "Referral Source",
      flex: isSm ? "unset" : 1,
      width: isSm ? 180 : undefined,
      headerClassName: "management-datagrid-registration",
    },
    {
      field: "company_size",
      headerName: "Company Size",
      flex: isSm ? "unset" : 1,
      width: isSm ? 170 : undefined,
      headerClassName: "management-datagrid-registration",
    },
    {
      field: "revenue_range",
      headerName: "Revenue Range",
      flex: isSm ? "unset" : 1,
      width: isSm ? 180 : undefined,
      headerClassName: "management-datagrid-registration",
    },
    {
      field: "top_projects",
      headerName: "Top Projects",
      flex: isSm ? "unset" : 1,
      width: isSm ? 160 : undefined,
      headerClassName: "management-datagrid-registration",
    },
    {
      field: "forms_you_like",
      headerName: "Forms User Like",
      flex: isSm ? "unset" : 1,
      width: isSm ? 180 : undefined,
      headerClassName: "management-datagrid-registration",
    },
    {
      field: "primary_goals",
      headerName: "Primary Goals",
      flex: isSm ? "unset" : 1,
      width: isSm ? 170 : undefined,
      headerClassName: "management-datagrid-registration",
    },
    {
      field: "average_noc",
      headerName: "Avg NOC",
      flex: isSm ? "unset" : 1,
      width: isSm ? 130 : undefined,
      headerClassName: "management-datagrid-registration",
    },
  ];

  const selections = registrationColumns.map((column) => column.headerName);

  return (
    <Card sx={{ minHeight: "35rem" }}>
      <CardHeader title={title} />
      <CardContent>
        <DataGrid
          sx={{
            "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
              outline: "none !important",
            },
            minHeight: "24rem",
          }}
          disableRowSelectionOnClick
          onCellClick={handleCellClick}
          classes={{
            cell: "cell", // Apply custom cell class
          }}
          rows={registrationRows}
          columns={registrationColumns}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 5,
              },
            },
          }}
          pageSizeOptions={[5, 10, 25, 50, 100]}
        />
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12}>
            <Typography sx={{ fontWeight: "bold" }}>
              {selectedCell && (
                <>
                  {selectedCell.colDef.headerName} -{" "}
                  <span style={{ fontWeight: "normal" }}>
                    {selectedCell.formattedValue}
                  </span>
                </>
              )}
            </Typography>
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              disabled={!selectedCell}
              label={!selectedCell ? "Select Cell" : "Enter New Value"}
              variant="filled"
              fullWidth
              value={textFieldValue || ""}
              onChange={(e) => setTextFieldValue(e.target.value)}
              InputProps={{
                endAdornment: selectedCell ? (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => {
                        setTextFieldValue("");
                        setSelectedCell(null);
                      }}
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <LoadingButton
              loading={editLoading}
              disabled={
                !selectedCell ||
                !textFieldValue ||
                textFieldValue === selectedCell?.formattedValue ||
                removeLoading ||
                addLoading
              }
              fullWidth
              variant="contained"
              color="warning"
              onClick={() => {
                handleEdit("edit");
              }}
            >
              Edit
            </LoadingButton>
          </Grid>
          <Grid item xs={12} md={2}>
            <LoadingButton
              loading={removeLoading}
              disabled={!selectedCell || editLoading || addLoading}
              fullWidth
              variant="contained"
              color="error"
              onClick={() => {
                handleEdit("remove");
              }}
            >
              Remove
            </LoadingButton>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth variant="filled">
              <Tooltip title="">
                <InputLabel
                  id={""}
                  sx={{
                    maxWidth: maxWidth, // Limit the label's width
                    whiteSpace: "nowrap", // Prevent label from wrapping
                    overflow: "hidden",
                    textOverflow: "ellipsis", // Add ellipsis for overflow
                  }}
                >
                  Select Category
                </InputLabel>
              </Tooltip>
              <Select
                labelId={""}
                id={""}
                label="Select Category"
                value={selectValue}
                onChange={(e) => setSelectValue(e.target.value)}
                MenuProps={{
                  PaperProps: {
                    style: {
                      marginTop: "1.5px",
                      maxHeight: 150, // Set the desired max height for the dropdown menu
                      width: "auto",
                      maxWidth: "100%",
                    },
                  },
                }}
                renderValue={renderSelectedValues}
                sx={{
                  maxWidth: "100%", // Set the desired max-width for the input element
                  "& .MuiTypography-body2": {
                    whiteSpace: "wrap", // Prevent text from wrapping or shrinking
                  },
                }}
                endAdornment={
                  selectValue !== "" ? ( // Place the clear icon to the right of the Select
                    (<IconButton
                      onClick={() => {
                        setSelectValue("");
                        setNewCellText("");
                      }}
                    >
                      <ClearIcon />
                    </IconButton>)
                  ) : undefined
                }
                IconComponent={selectValue !== "" ? "div" : undefined} // Remove the default MUI arrow
              >
                {selections?.map(function (item, i) {
                  const truncatedItem = truncateText(item, 30); // Truncate item if too long
                  return (
                    <MenuItem key={i} value={item}>
                      {item.length > 30 ? (
                        <Tooltip title={item}>
                          <Typography variant="body2">
                            {truncatedItem}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2">{item}</Typography>
                      )}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              disabled={!selectValue}
              label={"Enter Value"}
              variant="filled"
              fullWidth
              value={newCellText || ""}
              onChange={(e) => setNewCellText(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <LoadingButton
              loading={addLoading}
              disabled={!newCellText || removeLoading || editLoading}
              fullWidth
              variant="contained"
              color="primary"
              onClick={() => {
                handleEdit("add");
              }}
            >
              Add
            </LoadingButton>
          </Grid>
        </Grid>
        <Typography
          variant="body1"
          color="#F2CD00"
          fontWeight="bold"
          textAlign="center"
          sx={{ mt: "1rem", minHeight: "1.5rem" }}
        >
          {errorText}
        </Typography>
      </CardContent>
    </Card>
  );
};
